
export function initSliderPagination() {
  const sliders = document.querySelectorAll('.slider-block');
  if (!sliders.length) return;

  sliders.forEach((slider) => {
    const container = slider.querySelector('.slides-container');
    const content = slider.querySelector('.content');
    if (!container || !content) return;

    const items = Array.from(content.querySelectorAll('.slider-item'));
    if (items.length <= 1) return;

    const autoScroll = slider.dataset.autoscroll === 'true';
    const autoScrollSpeed = slider.dataset.autoscrollingSpeed || 'mod';

    const state = {
      currentPage: 0,
      pageCount: 0,
      containerWidth: 0,
      mouseDown: false,
      mouseDownLastPositionX: 0,
      containerTransformX: 0,
      mouseMoveLastXPosition: 0,
      increaseValue: 0,
      direction: 'left',
      scrollSpeed: 40,
      timeoutControl: null,
    };

    // ----------------------------
    // Helpers
    // ----------------------------

    function clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    function getActivePage() {
      return clamp(state.currentPage, 0, Math.max(0, state.pageCount - 1));
    }

    // Check if device has touch screen
    function hasTouchScreen() {
      if ('maxTouchPoints' in window.navigator) {
        return window.navigator.maxTouchPoints > 0;
      }
      if ('msMaxTouchPoints' in window.navigator) {
        return window.navigator.msMaxTouchPoints > 0;
      }
      const mQ = window.matchMedia?.('(pointer:coarse)');
      if (mQ?.media === '(pointer:coarse)') {
        return !!mQ.matches;
      }
      if ('orientation' in window) {
        return true;
      }
      const UA = window.navigator.userAgent;
      return (
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
        /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
      );
    }

    // Set scroll speed based on autoscrolling-speed attribute
    switch (autoScrollSpeed) {
      case 'fast':
        state.scrollSpeed = 15;
        break;
      case 'mod':
        state.scrollSpeed = 40;
        break;
      case 'slow':
        state.scrollSpeed = 75;
        break;
      default:
        state.scrollSpeed = 40;
    }

    // Calculate pages based on visible width
    function calculatePages() {
      state.containerWidth = content.offsetWidth;
      const totalWidth = items.reduce((acc, item) => acc + item.offsetWidth, 0);
      state.pageCount = Math.ceil(totalWidth / (state.containerWidth + 1));
      return state.pageCount;
    }

    // Estimate how many items fit in the viewport (used to "hide non-visible slides")
    function getItemsPerPage() {
      const w = state.containerWidth || content.offsetWidth || 1;
      let acc = 0;
      let count = 0;

      for (const item of items) {
        const iw = item.offsetWidth || 0;
        if (acc + iw > w && count > 0) break;
        acc += iw;
        count += 1;
      }

      return Math.max(1, count);
    }

    // ----------------------------
    // ADA/WCAG: Carousel semantics + live region + pause button
    // ----------------------------

    function ensureCarouselA11yScaffold() {
      // Container semantics
      slider.setAttribute('role', 'region');
      slider.setAttribute('aria-roledescription', 'carousel');

      // Accessible name: prefer data-slider-label; otherwise fallback
      // If you have a visible heading, consider using aria-labelledby instead.
      const label = slider.dataset.sliderLabel || 'Featured content';
      slider.setAttribute('aria-label', label);

      // Live region for announcing slide changes
      let status = slider.querySelector('.carousel-status');
      if (!status) {
        status = document.createElement('div');
        status.className = 'carousel-status sr-only';
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        // Must be early in reading order
        slider.prepend(status);
      }

      // Pause/Resume control required if there is automatic movement
      if (autoScroll) {
        let toggle = slider.querySelector('.carousel-toggle');
        if (!toggle) {
          toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'carousel-toggle';
          toggle.setAttribute('aria-pressed', 'false');
          toggle.textContent = 'Pause rotation';
          // Must precede the rest of the carousel in tab order
          slider.prepend(toggle);
        }

        toggle.addEventListener('click', () => {
          const pressed = toggle.getAttribute('aria-pressed') === 'true';
          if (pressed) {
            toggle.setAttribute('aria-pressed', 'false');
            toggle.textContent = 'Pause rotation';
            startAutoScroll();
          } else {
            toggle.setAttribute('aria-pressed', 'true');
            toggle.textContent = 'Resume rotation';
            stopAutoScroll();
          }
        });
      }
    }

    function announcePageChange() {
      const status = slider.querySelector('.carousel-status');
      if (!status) return;

      const page = getActivePage();
      status.textContent = `Slide ${page + 1} of ${state.pageCount}`;
    }

    // Hide non-visible content from screen readers and keyboard
    // Uses `hidden` which removes from accessibility tree and tab order.
    function updateSlidesVisibility() {
      // In auto-scroll mode, you are not using page-based scrolling.
      // Avoid hiding "real" items there unless you refactor.
      if (autoScroll && hasTouchScreen()) return;

      const itemsPerPage = getItemsPerPage();
      const page = getActivePage();
      const start = page * itemsPerPage;
      const end = start + itemsPerPage - 1;

      items.forEach((item, idx) => {
        // Provide slide semantics
        item.setAttribute('role', 'group');
        item.setAttribute('aria-roledescription', 'slide');
        item.setAttribute('aria-label', `${idx + 1} of ${items.length}`);

        const isVisible = idx >= start && idx <= end;
        if (isVisible) {
          item.removeAttribute('hidden');
          item.removeAttribute('aria-hidden');
        } else {
          item.setAttribute('hidden', '');
          item.setAttribute('aria-hidden', 'true');
        }
      });
    }

    // ----------------------------
    // Navigation
    // ----------------------------

    function scrollToPage(pageIndex) {
      const left = state.containerWidth * pageIndex;
      content.scroll({
        top: 0,
        left,
        behavior: 'smooth',
      });
      state.currentPage = pageIndex;
      updateUI();
    }

    function updateUI() {
      // Pagination (tablist)
      const dots = slider.querySelectorAll('.pagination-dot');
      dots.forEach((dot, index) => {
        const selected = index === state.currentPage;
        dot.classList.toggle('selected', selected);
        dot.setAttribute('aria-selected', selected ? 'true' : 'false');
        // Roving tabindex: only the selected tab is focusable with Tab
        dot.tabIndex = selected ? 0 : -1;
      });

      // Arrows must be *actually* disabled, not only visually
      const prevArrow = slider.querySelector('.arrow.prev');
      const nextArrow = slider.querySelector('.arrow.next');

      if (prevArrow) {
        const disabled = state.currentPage === 0;
        prevArrow.classList.toggle('disabled', disabled);
        prevArrow.disabled = disabled;
        prevArrow.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      if (nextArrow) {
        const disabled = state.currentPage >= state.pageCount - 1;
        nextArrow.classList.toggle('disabled', disabled);
        nextArrow.disabled = disabled;
        nextArrow.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      updateSlidesVisibility();
      announcePageChange();
    }

    // Create pagination dots as accessible buttons (tablist pattern)
    function createPagination() {
      // Remove old pagination if present
      const oldPagination = slider.querySelector('.pagination');
      if (oldPagination) oldPagination.remove();

      const paginationContainer = document.createElement('div');
      paginationContainer.className = 'pagination';
      paginationContainer.setAttribute('role', 'tablist');
      paginationContainer.setAttribute('aria-label', 'Choose slide');

      for (let i = 0; i < state.pageCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pagination-dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('aria-selected', i === state.currentPage ? 'true' : 'false');
        dot.tabIndex = i === state.currentPage ? 0 : -1;

        // Mouse
        dot.addEventListener('click', () => scrollToPage(i));

        // Keyboard: arrow keys to switch tabs (recommended for tablist)
        dot.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();

          const dir = e.key === 'ArrowRight' ? 1 : -1;
          const next = clamp(i + dir, 0, state.pageCount - 1);
          scrollToPage(next);

          // After UI updates, move focus to the active dot
          const dots = slider.querySelectorAll('.pagination-dot');
          dots[next]?.focus();
        });

        paginationContainer.appendChild(dot);
      }

      slider.appendChild(paginationContainer);
    }

    // Create navigation arrows in overlay container
    function createArrows() {
      let arrowsWrapper = slider.querySelector('.slider-arrows');
      if (!arrowsWrapper) {
        arrowsWrapper = document.createElement('div');
        arrowsWrapper.className = 'slider-arrows';
        slider.appendChild(arrowsWrapper);
      } else {
        // Prevent duplicates on re-init
        arrowsWrapper.innerHTML = '';
      }

      const prevArrow = document.createElement('button');
      prevArrow.type = 'button';
      prevArrow.className = 'arrow prev';
      prevArrow.setAttribute('aria-label', 'Previous slide');
      prevArrow.innerHTML = '<oportun-icon name="arrow-left"></oportun-icon>';
      prevArrow.addEventListener('click', () => {
        const newPage = clamp(state.currentPage - 1, 0, state.pageCount - 1);
        scrollToPage(newPage);
      });

      const nextArrow = document.createElement('button');
      nextArrow.type = 'button';
      nextArrow.className = 'arrow next';
      nextArrow.setAttribute('aria-label', 'Next slide');
      nextArrow.innerHTML = '<oportun-icon name="arrow-right"></oportun-icon>';
      nextArrow.addEventListener('click', () => {
        const newPage = clamp(state.currentPage + 1, 0, state.pageCount - 1);
        scrollToPage(newPage);
      });

      arrowsWrapper.appendChild(prevArrow);
      arrowsWrapper.appendChild(nextArrow);
    }

    function handleScroll() {
      const scrollLeft = content.scrollLeft;
      const newPage = Math.round(scrollLeft / state.containerWidth);
      if (newPage !== state.currentPage) {
        state.currentPage = newPage;
        updateUI();
      }
    }

    // ----------------------------
    // Auto-scroll logic (touch devices)
    // ----------------------------

    function cloneItemsForAutoScroll() {
      const totElements = items.length;
      const firstItem = items[0];
      const styles = window.getComputedStyle(firstItem);

      content.style.setProperty('--item-width', styles.width);
      content.style.setProperty('--items-carousel', totElements);

      items.forEach((item, index) => {
        const cloneItem = item.cloneNode(true);

        // ADA: clones must not be reachable by screen readers or keyboard
        cloneItem.setAttribute('aria-hidden', 'true');
        // Remove focusability from interactive elements inside clones
        cloneItem.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
          el.tabIndex = -1;
        });

        if (index === 0) {
          cloneItem.id = 'first-clone';
        } else if (index === totElements - 1) {
          cloneItem.id = 'last-clone';
        } else {
          cloneItem.id = `${index}-clone`;
        }

        content.appendChild(cloneItem);
      });
    }

    function handleGestureDown(event) {
      state.mouseDown = true;
      state.mouseDownLastPositionX = event.pageX;

      const contentTransform = window.getComputedStyle(content).getPropertyValue('transform');

      if (contentTransform !== 'none') {
        const transformX = parseInt(contentTransform.split(',')[4].trim(), 10);
        state.containerTransformX = transformX;
      }

      window.clearInterval(state.timeoutControl);
      content.style.cursor = 'grabbing';
    }

    function handleGestureMove(event) {
      if (state.mouseDown) {
        handleDrag(event.pageX);
      }
      state.mouseMoveLastXPosition = event.pageX;
    }

    function handleGestureUp() {
      state.mouseDown = false;
      content.style.cursor = 'grab';

      // Resume auto-scroll only if not paused (pause button uses stopAutoScroll)
      // We cannot reliably detect "paused" state without tracking; keep it simple:
      // if user wants pause, they can click Pause again after gesture.
      if (autoScroll && hasTouchScreen()) {
        startAutoScroll();
      }
    }

    function handleDrag(eventPageX = 0) {
      let mouseMoveDifference = 0;

      if (eventPageX) {
        mouseMoveDifference = eventPageX - state.mouseDownLastPositionX;
      }

      let translateValueX = mouseMoveDifference + state.containerTransformX;

      // Don't allow dragging out of left bounds
      if (translateValueX > 0) translateValueX = 0;

      // Don't allow dragging out of right bounds
      const rightSideFurthestCoordinates = content.scrollWidth - window.innerWidth;
      const contentSideMargin = parseInt(window.getComputedStyle(content).getPropertyValue('margin'), 10);

      const hasDraggedOutOfRightSideBounds =
        Math.abs(translateValueX) > rightSideFurthestCoordinates + contentSideMargin;

      if (hasDraggedOutOfRightSideBounds) {
        translateValueX = -(rightSideFurthestCoordinates + contentSideMargin);
      }

      state.increaseValue = translateValueX * -1;
      content.style.transform = `translateX(${translateValueX}px)`;
    }

    function compAutoScroll() {
      const lastClone = content.querySelector('#last-clone');
      if (!lastClone) return;

      const rect = lastClone.getBoundingClientRect();
      const screenSize = window.innerWidth || document.documentElement.clientWidth;

      if (state.direction === 'left') {
        if (rect.right <= screenSize - screenSize * 0.1) {
          state.direction = 'right';
        } else {
          state.increaseValue += 1;
        }
      } else {
        if (state.increaseValue === 0) {
          state.direction = 'left';
        } else {
          state.increaseValue -= 1;
        }
      }

      content.style.transform = `translateX(-${state.increaseValue}px)`;
    }

    function startAutoScroll() {
      window.clearInterval(state.timeoutControl);
      state.timeoutControl = window.setInterval(() => {
        compAutoScroll();
      }, state.scrollSpeed);
    }

    function stopAutoScroll() {
      window.clearInterval(state.timeoutControl);
    }

    // ----------------------------
    // Init
    // ----------------------------

    function init() {
      // ADA/WCAG scaffolding
      ensureCarouselA11yScaffold();

      if (autoScroll && hasTouchScreen()) {
        // Auto-scroll mode (touch)
        cloneItemsForAutoScroll();
        content.style.cursor = 'grab';

        // Pointer/mouse drag
        content.addEventListener('mousedown', handleGestureDown);
        content.addEventListener('mousemove', handleGestureMove);
        content.addEventListener('mouseup', handleGestureUp);
        content.addEventListener('mouseleave', handleGestureUp);

        content.addEventListener('pointerdown', handleGestureDown);
        content.addEventListener('pointermove', handleGestureMove);
        content.addEventListener('pointerup', handleGestureUp);
        content.addEventListener('pointerleave', handleGestureUp);

        window.addEventListener('mouseup', handleGestureUp);

        // Start auto-scroll
        startAutoScroll();

        slider._sliderCleanup = () => {
          stopAutoScroll();
          content.removeEventListener('mousedown', handleGestureDown);
          content.removeEventListener('mousemove', handleGestureMove);
          content.removeEventListener('mouseup', handleGestureUp);
          content.removeEventListener('mouseleave', handleGestureUp);
          content.removeEventListener('pointerdown', handleGestureDown);
          content.removeEventListener('pointermove', handleGestureMove);
          content.removeEventListener('pointerup', handleGestureUp);
          content.removeEventListener('pointerleave', handleGestureUp);
          window.removeEventListener('mouseup', handleGestureUp);
        };
      } else {
        // Normal pagination mode
        calculatePages();

        if (state.pageCount > 1) {
          createArrows();
          createPagination();
          // Initial state exposure
          updateUI();

          content.addEventListener('scroll', handleScroll);

          // Handle resize
          let resizeTimeout;
          const handleResize = () => {
            window.clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(() => {
              calculatePages();
              if (state.pageCount > 1) {
                createPagination();
                updateUI();
              }
            }, 300);
          };

          window.addEventListener('resize', handleResize);

          slider._sliderCleanup = () => {
            content.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
          };
        }
      }
    }

    init();
  });
}
